import { useEffect, useRef, useState, useCallback } from "react";
import { postSessionMessage } from "./sessionChannel";

// Client-side inactivity tracking (requirement: no backend calls per event).
// Meaningful activity (mouse, keyboard, scroll, touch) updates a ref — cheap,
// no re-renders — and a 1s interval derives the state machine:
//   active → warning (remaining <= warningBeforeSec) → expired (remaining <= 0)
// While the warning modal is up, raw local activity does NOT silently dismiss
// it — the user must click Stay Logged In (or another tab's activity vouches
// for them via the ACTIVITY broadcast).

export type SessionState = "active" | "warning" | "expired";

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "mousemove",
];
const BROADCAST_THROTTLE_MS = 15000;

export function useInactivityTimer(options: {
  enabled: boolean;
  idleTimeoutSec: number;
  warningBeforeSec: number;
}) {
  const { enabled, idleTimeoutSec, warningBeforeSec } = options;
  const lastActivityRef = useRef<number>(Date.now());
  const lastBroadcastRef = useRef<number>(0);
  const stateRef = useRef<SessionState>("active");
  const [state, setState] = useState<SessionState>("active");
  const [remainingSec, setRemainingSec] = useState<number>(idleTimeoutSec);

  const reset = useCallback(() => {
    lastActivityRef.current = Date.now();
    stateRef.current = "active";
    setState("active");
    setRemainingSec(idleTimeoutSec);
  }, [idleTimeoutSec]);

  // Activity vouched for by ANOTHER tab (or an explicit Stay Logged In there).
  const notifyExternalActivity = useCallback((at?: number) => {
    lastActivityRef.current = Math.max(lastActivityRef.current, at || Date.now());
    if (stateRef.current !== "active") {
      stateRef.current = "active";
      setState("active");
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const onActivity = () => {
      // Ignore raw activity while the warning modal is showing (explicit
      // Stay Logged In required) or after expiry.
      if (stateRef.current !== "active") return;
      const now = Date.now();
      lastActivityRef.current = now;
      if (now - lastBroadcastRef.current >= BROADCAST_THROTTLE_MS) {
        lastBroadcastRef.current = now;
        postSessionMessage({ type: "ACTIVITY" });
      }
    };

    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, onActivity, { passive: true })
    );

    const interval = window.setInterval(() => {
      const idleForSec = (Date.now() - lastActivityRef.current) / 1000;
      const remaining = Math.max(0, Math.round(idleTimeoutSec - idleForSec));
      setRemainingSec(remaining);
      const next: SessionState =
        remaining <= 0
          ? "expired"
          : remaining <= warningBeforeSec
          ? "warning"
          : "active";
      if (next !== stateRef.current) {
        stateRef.current = next;
        setState(next);
      }
    }, 1000);

    return () => {
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, onActivity)
      );
      window.clearInterval(interval);
    };
  }, [enabled, idleTimeoutSec, warningBeforeSec]);

  return { state, remainingSec, reset, notifyExternalActivity };
}
